/**
 * COUNTRY MANAGER
 * 
 * Admin component for managing the supported_countries table.
 * Allows adding, editing, removing, and reordering countries.
 */

import { useState, useEffect } from 'react';
import { logAdminAction } from '../../lib/adminAuth';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface SupportedCountry {
  id: number;
  country_code: string;
  country_name: string;
  flag_emoji: string;
  is_primary: boolean;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface CountryManagerProps {
  currentUser?: any;
}

export default function CountryManager({ currentUser }: CountryManagerProps) {
  const [countries, setCountries] = useState<SupportedCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState<SupportedCountry | null>(null);
  const [formData, setFormData] = useState({
    country_code: '',
    country_name: '',
    flag_emoji: '',
    is_primary: false,
    is_active: true,
    display_order: 999
  });

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'select',
          data: {
            table: 'supported_countries',
            select: '*',
            order: [
              { column: 'is_active', ascending: false },
              { column: 'display_order', ascending: true },
              { column: 'country_name', ascending: true }
            ]
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setCountries(result.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingCountry) {
        // Update existing
        const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            operation: 'update',
            data: {
              table: 'supported_countries',
              values: {
                country_code: formData.country_code.toUpperCase(),
                country_name: formData.country_name,
                flag_emoji: formData.flag_emoji,
                is_primary: formData.is_primary,
                is_active: formData.is_active,
                display_order: formData.display_order,
                updated_at: new Date().toISOString()
              },
              filters: { id: editingCountry.id }
            }
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error);

        await logAdminAction(currentUser?.email || 'unknown', 'update_country', 'supported_countries', String(editingCountry.id), {
          country_code: formData.country_code,
          country_name: formData.country_name
        });

        setSuccess(`Updated ${formData.country_name}`);
      } else {
        // Check for duplicate
        const checkResponse = await fetch(`${API_BASE_URL}/api/admin/generic`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            operation: 'select',
            data: {
              table: 'supported_countries',
              select: 'id',
              filters: { country_code: formData.country_code.toUpperCase() },
              limit: 1
            }
          })
        });
        const checkResult = await checkResponse.json();
        if (checkResult.data && checkResult.data.length > 0) {
          throw new Error(`Country with code ${formData.country_code} already exists`);
        }

        // Insert new
        const insertResponse = await fetch(`${API_BASE_URL}/api/admin/generic`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            operation: 'insert',
            data: {
              table: 'supported_countries',
              row: {
                country_code: formData.country_code.toUpperCase(),
                country_name: formData.country_name,
                flag_emoji: formData.flag_emoji,
                display_order: formData.display_order
              }
            }
          })
        });
        const insertResult = await insertResponse.json();
        if (!insertResponse.ok) throw new Error(insertResult.error);

        await logAdminAction(currentUser?.email || 'unknown', 'add_country', 'supported_countries', insertResult.data?.id, {
          country_code: formData.country_code,
          country_name: formData.country_name
        });

        setSuccess(`Added ${formData.country_name}`);
      }

      resetForm();
      loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save country');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (country: SupportedCountry) => {
    if (!confirm(`Are you sure you want to delete ${country.country_name}? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'delete',
          data: {
            table: 'supported_countries',
            filters: { id: country.id }
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await logAdminAction(currentUser?.email || 'unknown', 'delete_country', 'supported_countries', String(country.id), {
        country_code: country.country_code,
        country_name: country.country_name
      });

      setSuccess(`Deleted ${country.country_name}`);
      loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete country');
    }
  };

  const toggleActive = async (country: SupportedCountry) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update',
          data: {
            table: 'supported_countries',
            values: { is_active: !country.is_active, updated_at: new Date().toISOString() },
            filters: { id: country.id }
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await logAdminAction(currentUser?.email || 'unknown', country.is_active ? 'deactivate_country' : 'activate_country',
        'supported_countries', String(country.id), { country_code: country.country_code });

      loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle country status');
    }
  };

  const togglePrimary = async (country: SupportedCountry) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update',
          data: {
            table: 'supported_countries',
            values: { is_primary: !country.is_primary, updated_at: new Date().toISOString() },
            filters: { id: country.id }
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle primary status');
    }
  };

  const resetForm = () => {
    setFormData({
      country_code: '',
      country_name: '',
      flag_emoji: '',
      is_primary: false,
      is_active: true,
      display_order: 999
    });
    setEditingCountry(null);
    setShowAddModal(false);
  };

  const openEditModal = (country: SupportedCountry) => {
    setEditingCountry(country);
    setFormData({
      country_code: country.country_code,
      country_name: country.country_name,
      flag_emoji: country.flag_emoji,
      is_primary: country.is_primary,
      is_active: country.is_active,
      display_order: country.display_order
    });
    setShowAddModal(true);
  };

  // Filter countries
  const filteredCountries = countries.filter(country => {
    const matchesSearch = !searchQuery ||
      country.country_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.country_code.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && country.is_active) ||
      (filterStatus === 'inactive' && !country.is_active);

    return matchesSearch && matchesStatus;
  });

  const activeCount = countries.filter(c => c.is_active).length;
  const primaryCount = countries.filter(c => c.is_primary).length;

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">🌍 Country Manager</h1>
          <p className="text-gray-300">Manage supported countries that users can select during registration</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{countries.length}</div>
            <div className="text-white text-sm">Total Countries</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-green-700">
            <div className="text-2xl font-bold text-green-400">{activeCount}</div>
            <div className="text-white text-sm">Active Countries</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-yellow-700">
            <div className="text-2xl font-bold text-yellow-400">{primaryCount}</div>
            <div className="text-white text-sm">Primary Countries (Top of List)</div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">
            <strong>Error:</strong> {error}
            <button onClick={() => setError(null)} className="float-right text-red-300 hover:text-white">×</button>
          </div>
        )}
        {success && (
          <div className="bg-green-900/50 border border-green-700 text-green-200 px-4 py-3 rounded-lg mb-4">
            <strong>Success:</strong> {success}
            <button onClick={() => setSuccess(null)} className="float-right text-green-300 hover:text-white">×</button>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap gap-4 items-center justify-between mb-6 bg-gray-800 p-4 rounded-lg">
          <div className="flex gap-3 items-center">
            <input
              type="text"
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white w-64"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="bg-yellow-600 hover:bg-yellow-700 px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
          >
            <span>➕</span> Add Country
          </button>
        </div>

        {/* Countries Table */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
              <p className="mt-3 text-white">Loading countries...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Flag</th>
                    <th className="text-left py-3 px-4 font-semibold">Country Name</th>
                    <th className="text-left py-3 px-4 font-semibold">Code</th>
                    <th className="text-center py-3 px-4 font-semibold">Order</th>
                    <th className="text-center py-3 px-4 font-semibold">Primary</th>
                    <th className="text-center py-3 px-4 font-semibold">Status</th>
                    <th className="text-center py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCountries.map((country) => (
                    <tr key={country.id} className={`border-b border-gray-700 hover:bg-gray-750 ${!country.is_active ? 'opacity-50' : ''}`}>
                      <td className="py-3 px-4 text-2xl">{country.flag_emoji}</td>
                      <td className="py-3 px-4 font-medium">{country.country_name}</td>
                      <td className="py-3 px-4 text-white font-mono">{country.country_code}</td>
                      <td className="py-3 px-4 text-center text-white">{country.display_order}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => togglePrimary(country)}
                          className={`px-3 py-1 rounded text-sm ${country.is_primary ? 'bg-yellow-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                        >
                          {country.is_primary ? '⭐ Primary' : 'Regular'}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => toggleActive(country)}
                          className={`px-3 py-1 rounded text-sm ${country.is_active ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
                        >
                          {country.is_active ? '✓ Active' : '✗ Inactive'}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => openEditModal(country)}
                            className="text-blue-400 hover:text-blue-300 px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(country)}
                            className="text-red-400 hover:text-red-300 px-2 py-1"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredCountries.length === 0 && (
                <div className="text-center py-12 text-white">
                  {searchQuery ? 'No countries match your search.' : 'No countries found. Add your first country.'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <h2 className="text-xl font-bold text-yellow-400 mb-6">
              {editingCountry ? '✏️ Edit Country' : '➕ Add New Country'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white mb-1">Country Code (ISO 3166)</label>
                  <input
                    type="text"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                    maxLength={3}
                    required
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white uppercase"
                    placeholder="ZAF"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Flag Emoji</label>
                  <input
                    type="text"
                    value={formData.flag_emoji}
                    onChange={(e) => setFormData({ ...formData, flag_emoji: e.target.value })}
                    required
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center text-xl"
                    placeholder="🇿🇦"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-white mb-1">Country Name</label>
                <input
                  type="text"
                  value={formData.country_name}
                  onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="South Africa"
                />
              </div>

              <div>
                <label className="block text-sm text-white mb-1">Display Order (lower = higher priority)</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 999 })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  min="1"
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                    className="w-4 h-4 accent-yellow-500"
                  />
                  <span className="text-sm">Primary (show at top)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 accent-green-500"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingCountry ? 'Update Country' : 'Add Country')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

